import React, { useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { convertTimestampFormatZ } from "../../scripts/utils";
import { useQuery } from "@tanstack/react-query";
import ChartUnicast from "./Charts/ChartUnicast";
import ChartBandwidth from "./Charts/ChartBandwidth";
import ChartInterfaces from "./Charts/ChartInterfaces";
import { DateRangePicker } from "rsuite";
import { startOfDay, endOfDay, addDays, subDays } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader";

export default function InterfaceAuvik() {
    const { apiUrl } = useContext(AuthContext);
    const { tenantId, interfaceId } = useParams();
    const { allowedMaxDays, afterToday, combine } = DateRangePicker;
    const today = endOfDay(new Date(), 23);
    const yesterday = startOfDay(addDays(new Date(), -1), 0);
    const [date, setDate] = useState([yesterday, today]);
    const { theme } = useTheme();
    const { t } = useTranslation();

    const ranges = [
        {
            label: t("Today"),
            value: [startOfDay(new Date()), endOfDay(new Date())],
        },
        {
            label: t("Yesterday"),
            value: [startOfDay(addDays(new Date(), -1)), endOfDay(addDays(new Date(), -1))],
        },
        {
            label: t("Last seven days"),
            value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
        }
    ];

    const [startDate, endDate] = date.map(convertTimestampFormatZ);

    const getInterfaceData = async (metricName) => {
        const res = await axios.get(`${apiUrl}auvik/interfaceStat/${metricName}/${tenantId}/${interfaceId}/${startDate}/${endDate}`, {
            withCredentials: true,
        });
        return res.data;
    };

    const bandwidth = useQuery({
        queryKey: ["bandwidth", date, interfaceId],
        queryFn: () => getInterfaceData("bandwidth"),
    });

    const utilization = useQuery({
        queryKey: ["utilization", date, interfaceId],
        queryFn: () => getInterfaceData("utilization"),
    });

    const packetLoss = useQuery({
        queryKey: ["packetLoss", date, interfaceId],
        queryFn: () => getInterfaceData("packetLoss"),
    });

    const packetDiscard = useQuery({
        queryKey: ["packetDiscard", date, interfaceId],
        queryFn: () => getInterfaceData("packetDiscard"),
    });

    const packetUnicast = useQuery({
        queryKey: ["packetUnicast", date, interfaceId],
        queryFn: () => getInterfaceData("packetUnicast"),
    });

    const packetMulticast = useQuery({
        queryKey: ["packetMulticast", date, interfaceId],
        queryFn: () => getInterfaceData("packetMulticast"),
    });

    const packetBroadcast = useQuery({
        queryKey: ["packetBroadcast", date, interfaceId],
        queryFn: () => getInterfaceData("packetBroadcast"),
    });

    const hasPointValues = (dataset) =>
        Array.isArray(dataset) &&
        dataset.some((item) =>
            Object.entries(item).some(([key, value]) => key !== "RecordedAt" && value !== null && value !== undefined)
        );

    const isReady = [
        bandwidth.data,
        utilization.data,
        packetUnicast.data,
        packetMulticast.data,
        packetBroadcast.data,
        packetLoss.data,
        packetDiscard.data
    ].every(Array.isArray);

    if (!isReady) {
        return <Loader />;
    }

    return (
        <div className="charts-content">
            <section className="statistics-auvik">
                <div className="page-title-auvik-interface">
                    <Link to={`/auvik/${tenantId}`} className="button button-new"><FontAwesomeIcon icon={faChevronLeft} /> {t("Retour")}</Link>
                    <h2>Interface</h2>
                </div>
                <div className="auvik-charts">
                    <div className="auvik-charts-container">
                        <div className="auvik-charts-container-page-section-form">
                            <div className="form-input_container">
                                <DateRangePicker
                                    ranges={ranges}
                                    cleanable={false}
                                    character=" / "
                                    value={date}
                                    onChange={setDate}
                                    format="yyyy-MM-dd HH:mm"
                                    shouldDisableDate={combine(allowedMaxDays(7), afterToday())}
                                    defaultValue={[yesterday, today]}
                                    locale={{
                                        sunday: t("Su"),
                                        monday: t("Mo"),
                                        tuesday: t("Tu"),
                                        wednesday: t("We"),
                                        thursday: t("Th"),
                                        friday: t("Fr"),
                                        saturday: t("Sa"),
                                        hours: t("Hours"),
                                    }}
                                />
                            </div>
                        </div>
                        {!hasPointValues(bandwidth.data) ? (
                            <h2 className="page-title">{t("Aucune donnée de bande passante trouvée")}</h2>
                        ) : (
                            <ChartBandwidth dataBandwidth={bandwidth.data} theme={theme} />
                        )}
                        {!hasPointValues(packetUnicast.data) && !hasPointValues(packetMulticast.data) && !hasPointValues(packetBroadcast.data) ? (
                            <h2 className="page-title">{t("Aucune donnée de paquets trouvée")}</h2>
                        ) : (
                            <ChartUnicast
                                packetUnicast={packetUnicast.data}
                                packetMulticast={packetMulticast.data}
                                packetBroadcast={packetBroadcast.data}
                                theme={theme}
                            />
                        )}
                        {!hasPointValues(utilization.data) ? (
                            <h2 className="page-title">{t("Aucune donnée d'utilisation trouvée")}</h2>
                        ) : (
                            <ChartInterfaces
                                datasInterface={utilization.data}
                                measurementUnit="%"
                                titleChartInterface={"Utilisation de l'appareil"}
                                theme={theme}
                            />
                        )}
                        {!hasPointValues(packetLoss.data) ? (
                            <h2 className="page-title">{t("Aucune donnée de perte de paquets trouvée")}</h2>
                        ) : (
                            <ChartInterfaces
                                datasInterface={packetLoss.data}
                                measurementUnit="/s"
                                titleChartInterface={"Perte de paquets de l'appareil"}
                                theme={theme}
                            />
                        )}
                        {!hasPointValues(packetDiscard.data) ? (
                            <h2 className="page-title">{t("Aucune donnée de paquets rejetés trouvée")}</h2>
                        ) : (
                            <ChartInterfaces
                                datasInterface={packetDiscard.data}
                                measurementUnit="/s"
                                titleChartInterface={"Paquets rejetés de l'appareil"}
                                theme={theme}
                            />
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
